import { getMeta } from '../storage/safe-store';

/**
 * Whether the auth page opens on "Create your safe" or "Welcome back".
 *
 * The mobile apps and the extension both open on create-your-safe for a device
 * with no account signal; web used to always open on login, burying signup in a
 * link at the bottom. This module holds the decision.
 */

/**
 * Set once an account has been used in this browser. Survives logout — see
 * PRESERVE_ON_LOGOUT in storage/safe-store.ts.
 *
 * Deliberately a boolean, not the email. clearAllLocalData() wipes hbs:email on
 * logout, so SOMETHING has to persist or a user who logged out would be asked to
 * create a second account. Preserving an address instead would leave it on disk
 * after logout — a privacy regression on a shared or family computer, and a
 * silent change to what "log out" means.
 */
export const HAS_ACCOUNT_KEY = 'hbs:hasAccount';

/** Record that an account has been used here. Called on login AND registration. */
export function markAccountUsed(): void {
  try {
    localStorage.setItem(HAS_ACCOUNT_KEY, '1');
  } catch {
    /* storage blocked → the signal is simply unavailable next time */
  }
}

/**
 * True only when nothing suggests this browser has an account.
 *
 * `fromPathname` is AuthGuard's `state.from` — the protected route the visitor
 * was bounced off. It counts as evidence ONLY when deeper than the app root:
 * marketing's sole app link is `/safe/`, and `/` redirects there too, so every
 * ad click arrives carrying from=/safe. Counting that would classify every new
 * visitor as returning and make this feature a no-op for its whole audience.
 *
 * Fails toward LOGIN when storage is unreadable. Wrongly showing login to a new
 * user costs one click on a link already on the page; wrongly showing create to
 * an existing user tells them their account does not exist.
 */
export function shouldOpenInCreateMode(fromPathname: string | undefined): boolean {
  if (isDeepLink(fromPathname)) return false;
  try {
    if (localStorage.getItem(HAS_ACCOUNT_KEY)) return false;
    if (getMeta('email')) return false;
    return true;
  } catch {
    return false;
  }
}

/** Anything below the app's landing routes implies the visitor had an account. */
function isDeepLink(pathname: string | undefined): boolean {
  if (!pathname) return false;
  const normalised = pathname.replace(/\/+$/, '');
  return normalised !== '' && normalised !== '/safe';
}
